/**
 * Etiquetas de campo renombrables por grupo.
 *
 * Los "aux ids" nacieron como campos comodín para que cada cliente les diera el uso que
 * quisiera, pero su nombre visible estaba clavado en el JSON de i18n. El backend
 * (catálogo PATIENT_FIELDS) resuelve el override del grupo del usuario logueado y lo
 * devuelve por código; aquí se traduce ese código a las claves de i18n concretas que
 * pinta cada pantalla.
 *
 * Un código pinta en VARIAS claves (ficha, cabecera de tabla, filtro, formulario), así
 * que el admin configura una sola etiqueta y todas las superficies la siguen.
 *
 * El mapa es curado a mano a propósito, NO plantillado con `${code}`:
 *  - `filter.state.title` es "Estado del servicio", otro concepto que casualmente
 *    comparte el nombre `state`. NO se sobrescribe.
 *  - `idCard` tiene dos claves para el mismo campo (`text.field.identityDocument` en la
 *    ficha, la cola de alarmas y el alta; `text.field.idCard` en el selector de campos
 *    obligatorios del grupo). Se sobrescriben las dos.
 */
import messages from 'i18n/messages';

export const FIELD_LABEL_I18N_KEYS: Record<string, string[]> = {
  principalAuxId: [
    'user.personalData.principalAuxId',
    'table.column.header.principalAuxId',
    'filter.principalAuxId.title',
    'text.field.principalAuxId',
  ],
  secondaryAuxId: [
    'user.personalData.secondaryAuxId',
    'table.column.header.secondaryAuxId',
    'filter.secondaryAuxId.title',
    'text.field.secondaryAuxId',
    'measures.heatmap.secondaryAuxId',
  ],
  idCard: ['text.field.identityDocument', 'text.field.idCard'],
  state: ['user.personalData.state', 'text.field.state'],
  county: ['user.personalData.county', 'text.field.county'],
};

/**
 * Códigos renombrables, para ofrecerlos en el autocompletado del admin. Debe cuadrar con
 * `PatientFieldLabels.overridableCodes()` del backend: un código de más aquí solo sale
 * como sugerencia sin efecto, pero uno de menos hace que el admin lo teclee a ciegas.
 */
export const PATIENT_FIELD_CODES = Object.keys(FIELD_LABEL_I18N_KEYS);

/**
 * Espejo de los overrides ya expandidos a claves de i18n, para el camino NO reactivo:
 * `i18n()` (utils/i18n.js) es una función suelta que lee el JSON importado y no puede
 * consultar el store sin crear un ciclo de imports.
 *
 * La fuente de verdad es el slice `fieldLabels`; esto es una proyección que actualiza
 * su thunk. El camino reactivo (`i18Intl()` / `<FormattedMessage>`) no pasa por aquí:
 * lo alimenta `Wrapper.js` mergeando sobre los mensajes del `IntlProvider`.
 */
let overridesByKey: Record<string, string> = {};

/** Expande `{code: label}` a `{claveI18n: label}`. Códigos desconocidos se ignoran. */
export const expandFieldLabels = (labelsByCode: Record<string, string> | null | undefined) => {
  const expanded: Record<string, string> = {};
  Object.entries(labelsByCode ?? {}).forEach(([code, label]) => {
    if (!label) {
      return;
    }
    (FIELD_LABEL_I18N_KEYS[code] ?? []).forEach((key) => {
      expanded[key] = label;
    });
  });
  return expanded;
};

export const setFieldLabelOverrides = (labelsByCode: Record<string, string> | null | undefined) => {
  overridesByKey = expandFieldLabels(labelsByCode);
};

/**
 * Literal de fábrica de un código, leído del JSON directamente.
 *
 * No sirve `i18n()` para esto: devolvería el override del grupo del usuario logueado, y
 * quien configura un grupo necesita ver el valor por defecto real, no el suyo.
 */
export const getDefaultFieldLabel = (code: string, locale: string): string => {
  const key = FIELD_LABEL_I18N_KEYS[code]?.[0];
  const dictionary: Record<string, string> = (messages as any)[locale] ?? {};
  return (key && dictionary[key]) || code;
};

export const getFieldLabelOverride = (key: string): string | undefined => overridesByKey[key];
